import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Phase 1 of Phone Management. Design doc:
// docs/design/PHONE_MANAGEMENT_2026-07-24.md.
// Single approver workflow, self-approve blocked. AWS sync + ASSIGN + UNASSIGN
// live now; Phase 2 (provision / release / TwiML binding) still a stub.

const TABS = [
  { key: 'inventory', label: 'Inventory' },
  { key: 'by-practice', label: 'By Practice' },
  { key: 'requests', label: 'Requests' },
  { key: 'audit', label: 'Audit' },
];

function PhoneManagement() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('inventory');
  const [envInfo, setEnvInfo] = useState(null);

  useEffect(() => {
    api.phoneEnvInfo().then(res => { if (res?.success) setEnvInfo(res.data); }).catch(() => {});
  }, []);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Phone Management</h2>
          <p style={s.subtitle}>Super-admin control surface for SMS + voice phone numbers across AWS and Twilio.</p>
        </div>
        {envInfo && (
          <div style={s.envBadge}>
            <span style={s.envKey}>ENV</span>
            <span style={s.envVal(envInfo.env)}>{envInfo.env.toUpperCase()}</span>
            <span style={s.envHint}>
              AWS pool: <code>{envInfo.aws_pool_id.slice(0, 20)}…</code> ·
              TwiML app: {envInfo.twiml_app_friendly_name}{envInfo.twiml_app_configured ? '' : ' ⚠ SID not set'}
            </span>
          </div>
        )}
      </div>

      <div style={s.tabbar}>
        {TABS.map(t => (
          <button key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{ ...s.tab, ...(activeTab === t.key ? s.tabActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={s.body}>
        {activeTab === 'inventory' && <InventoryTab currentUserId={user?.user?.id} />}
        {activeTab === 'by-practice' && <ByPracticeTab />}
        {activeTab === 'requests' && <RequestsTab currentUserId={user?.user?.id} />}
        {activeTab === 'audit' && <AuditTab />}
      </div>
    </div>
  );
}

function InventoryTab({ currentUserId }) {
  const [rows, setRows] = useState([]);
  const [env, setEnv] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [actionFor, setActionFor] = useState(null); // { row, action }
  const [showProvision, setShowProvision] = useState(false);
  const [filter, setFilter] = useState('all'); // all | assigned | unassigned
  const [drift, setDrift] = useState([]);
  const [driftOpen, setDriftOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.phoneInventory(filter !== 'all' ? { assigned_only: filter === 'assigned' } : {});
      if (res?.success) { setRows(res.data.rows || []); setEnv(res.data.env); }
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.phoneDrift().then(res => {
      if (res?.success) setDrift(res.data || []);
    }).catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.phoneSync();
      if (res?.success) {
        alert(`AWS: ${res.data.aws.synced} numbers. Twilio: ${res.data.twilio.note || res.data.twilio.synced + ' synced'}.`);
        await load();
      } else {
        alert('Sync failed: ' + (res?.error || 'unknown'));
      }
    } catch (err) {
      alert('Sync error: ' + err.message);
    } finally { setSyncing(false); }
  };

  return (
    <div>
      <div style={s.toolbar}>
        <select value={filter} onChange={e => setFilter(e.target.value)} style={s.select}>
          <option value="all">All numbers</option>
          <option value="assigned">Assigned only</option>
          <option value="unassigned">Unassigned only</option>
        </select>
        <button onClick={load} style={s.btnSecondary} disabled={loading}>Refresh</button>
        <button onClick={handleSync} style={s.btnPrimary} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync from providers'}
        </button>
        <button onClick={() => setShowProvision(true)} style={s.btnPrimary}>+ Provision New</button>
      </div>
      {drift.length > 0 && (
        <div style={s.driftBanner}>
          <div>
            ⚠ <strong>{drift.length}</strong> item{drift.length === 1 ? '' : 's'} out of sync with provider(s).
            {' '}Sync-from-providers will import new numbers; drift caused by external changes stays until a human resolves it.
          </div>
          <button style={s.btnLink} onClick={() => setDriftOpen(o => !o)}>{driftOpen ? 'Hide' : 'Show'}</button>
        </div>
      )}
      {driftOpen && (
        <table style={s.tableCompact}>
          <thead>
            <tr>
              <th style={s.th}>Provider</th>
              <th style={s.th}>Number</th>
              <th style={s.th}>Delta</th>
              <th style={s.th}>Details</th>
            </tr>
          </thead>
          <tbody>
            {drift.slice(0, 50).map((d, i) => (
              <tr key={i} style={s.tr}>
                <td style={s.td}>{d.provider.toUpperCase()}</td>
                <td style={s.td}>{d.number || <span style={s.muted}>—</span>}</td>
                <td style={s.td}><code>{d.delta_type}</code></td>
                <td style={s.td}><pre style={s.pre}>{JSON.stringify(d.details, null, 2)}</pre></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={s.hint}>Showing {rows.length} row{rows.length === 1 ? '' : 's'} in env <strong>{env}</strong></div>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Provider</th>
            <th style={s.th}>Number</th>
            <th style={s.th}>Type</th>
            <th style={s.th}>Pool / TwiML</th>
            <th style={s.th}>Assigned to</th>
            <th style={s.th}>Role</th>
            <th style={s.th}>2-way</th>
            <th style={s.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={s.tr}>
              <td style={s.td}>{r.provider.toUpperCase()}</td>
              <td style={s.td}><code>{r.number_e164}</code></td>
              <td style={s.td}>{r.type}</td>
              <td style={s.td}>{(() => {
                const val = r.provider === 'aws' ? r.pool_id : r.twiml_app_sid;
                if (!val) return <span style={s.muted}>—</span>;
                return val.length > 20 ? val.slice(0, 20) + '…' : val;
              })()}</td>
              <td style={s.td}>{r.practice_name || <span style={s.muted}>—</span>}</td>
              <td style={s.td}>{r.assigned_role || <span style={s.muted}>—</span>}</td>
              <td style={s.td}>{r.two_way_enabled ? '✓' : '—'}</td>
              <td style={s.td}>
                <RowActions
                  row={r}
                  onAssign={() => setAssignFor(r)}
                  onAction={(action) => setActionFor({ row: r, action })}
                  onDone={load}
                />
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', padding: 24, color: '#6b7280' }}>
              No numbers. Click "Sync from providers" to import the current inventory.
            </td></tr>
          )}
        </tbody>
      </table>
      {assignFor && (
        <AssignModal
          row={assignFor}
          onClose={() => setAssignFor(null)}
          onSubmitted={() => { setAssignFor(null); load(); }}
        />
      )}
      {actionFor && (
        <ActionModal
          row={actionFor.row}
          action={actionFor.action}
          env={env}
          onClose={() => setActionFor(null)}
          onSubmitted={() => { setActionFor(null); load(); }}
        />
      )}
      {showProvision && (
        <ProvisionModal
          env={env}
          onClose={() => setShowProvision(false)}
          onSubmitted={() => { setShowProvision(false); load(); }}
        />
      )}
    </div>
  );
}

function RowActions({ row, onAssign, onAction, onDone }) {
  const [open, setOpen] = useState(false);
  const canUnassign = !!row.assigned_practice_id;
  const isAws = row.provider === 'aws';
  const isTwilio = row.provider === 'twilio';
  const handleUnassign = async () => {
    const notes = window.prompt(`Reason for unassigning ${row.number_e164}?`);
    if (notes == null) return;
    try {
      const res = await api.phoneCreateRequest({
        action: 'UNASSIGN',
        target_number_id: row.id,
        target_practice_id: row.assigned_practice_id,
        params: {},
        requester_notes: notes || 'Unassign',
      });
      if (res?.success) {
        alert('Unassign request submitted for approval.');
        onDone?.();
      } else {
        alert('Failed: ' + (res?.error || 'unknown'));
      }
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };
  return (
    <div style={{ position: 'relative' }}>
      <button style={s.btnSecondary} onClick={() => setOpen(o => !o)}>Actions ▾</button>
      {open && (
        <div style={s.menu} onMouseLeave={() => setOpen(false)}>
          {!canUnassign && (
            <button style={s.menuItem} onClick={() => { setOpen(false); onAssign(); }}>Assign to practice…</button>
          )}
          {canUnassign && (
            <button style={s.menuItem} onClick={() => { setOpen(false); handleUnassign(); }}>Unassign</button>
          )}
          {isAws && (
            <>
              <button style={s.menuItem} onClick={() => { setOpen(false); onAction('MOVE_POOL'); }}>Move to different pool (same-env)…</button>
              <button style={s.menuItem} onClick={() => { setOpen(false); onAction('SET_SNS_TOPIC'); }}>Change SNS topic…</button>
              <button style={s.menuItem} onClick={() => { setOpen(false); onAction('ENABLE_TWO_WAY'); }}>Toggle two-way inbound…</button>
            </>
          )}
          {isTwilio && (
            <button style={s.menuItem} onClick={() => { setOpen(false); onAction('SET_TWIML_APP'); }}>Rebind TwiML app (env-locked)…</button>
          )}
          <button style={{ ...s.menuItem, color: '#dc2626' }} onClick={() => { setOpen(false); onAction('RELEASE_NUMBER'); }}>Release number (destructive)…</button>
        </div>
      )}
    </div>
  );
}

function ActionModal({ row, action, env, onClose, onSubmitted }) {
  const [notes, setNotes] = useState('');
  const [params, setParams] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const title = {
    RELEASE_NUMBER: `Release ${row.number_e164}`,
    MOVE_POOL: `Move ${row.number_e164} to a different pool (same-env only)`,
    SET_SNS_TOPIC: `Change SNS topic for ${row.number_e164}`,
    ENABLE_TWO_WAY: `Toggle two-way inbound on ${row.number_e164}`,
    SET_TWIML_APP: `Rebind ${row.number_e164} to the env's TwiML app`,
  }[action] || `Action: ${action}`;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.phoneCreateRequest({
        action,
        target_number_id: row.id,
        target_practice_id: row.assigned_practice_id || null,
        params,
        requester_notes: notes,
      });
      if (res?.success) {
        alert('Change request submitted for approval.');
        onSubmitted?.();
      } else {
        alert('Failed: ' + (res?.error || 'unknown'));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitle}>{title}</h3>
        {action === 'RELEASE_NUMBER' && (
          <div style={s.warnBox}>
            ⚠ This action is IRREVERSIBLE. The number is returned to the AWS/Twilio pool of available numbers and cannot be reclaimed.
            The approver must type <code>RELEASE PERMANENTLY</code> in the approval notes to authorize.
          </div>
        )}
        {action === 'MOVE_POOL' && (
          <>
            <div style={s.warnBox}>Cross-env moves (QA ↔ prod) are blocked from this UI. Use the AWS console for that.</div>
            <div style={s.field}>
              <label style={s.label}>Destination pool ID (must be a {env}-tagged pool)</label>
              <input type="text" placeholder="pool-…" value={params.dest_pool_id || ''} onChange={e => setParams({ ...params, dest_pool_id: e.target.value })} style={s.input} />
            </div>
          </>
        )}
        {action === 'SET_SNS_TOPIC' && (
          <div style={s.field}>
            <label style={s.label}>New SNS topic ARN</label>
            <input type="text" placeholder="arn:aws:sns:us-west-2:…" value={params.sns_topic_arn || ''} onChange={e => setParams({ ...params, sns_topic_arn: e.target.value })} style={s.input} />
          </div>
        )}
        {action === 'ENABLE_TWO_WAY' && (
          <>
            <div style={s.field}>
              <label style={s.label}>State</label>
              <select value={params.enabled === false ? 'off' : 'on'} onChange={e => setParams({ ...params, enabled: e.target.value === 'on' })} style={s.select}>
                <option value="on">Enable two-way (route inbound to SNS)</option>
                <option value="off">Disable two-way</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>SNS topic ARN (leave blank to use env default)</label>
              <input type="text" placeholder="arn:aws:sns:…" value={params.sns_topic_arn || ''} onChange={e => setParams({ ...params, sns_topic_arn: e.target.value })} style={s.input} />
            </div>
          </>
        )}
        {action === 'SET_TWIML_APP' && (
          <div style={s.warnBox}>The env-locked TwiML app SID is used automatically (NH-QA or NH-PROD). No free-text entry — prevents cross-env mistakes.</div>
        )}
        <div style={s.field}>
          <label style={s.label}>Notes (why)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={s.textarea} />
        </div>
        <div style={s.modalActions}>
          <button style={s.btnSecondary} onClick={onClose}>Cancel</button>
          <button style={s.btnPrimary} onClick={submit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProvisionModal({ env, onClose, onSubmitted }) {
  const [provider, setProvider] = useState('aws');
  const [type, setType] = useState('LONG_CODE');
  const [areaCode, setAreaCode] = useState('');
  const [autoAssignPracticeId, setAutoAssignPracticeId] = useState('');
  const [role, setRole] = useState('sms_out_in');
  const [notes, setNotes] = useState('');
  const [practices, setPractices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.request('/practices').then(res => {
      if (res?.success) setPractices((res.data || []).filter(p => p.enabled));
    });
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.phoneCreateRequest({
        action: 'PROVISION_NUMBER',
        params: {
          provider,
          type,
          area_code: areaCode || null,
          iso_country_code: 'US',
          message_type: 'TRANSACTIONAL',
          capabilities: provider === 'aws' ? ['SMS'] : ['VOICE'],
          auto_assign_practice_id: autoAssignPracticeId ? Number(autoAssignPracticeId) : null,
          role: autoAssignPracticeId ? role : null,
        },
        requester_notes: notes,
      });
      if (res?.success) {
        alert('Provision request submitted for approval. AWS/Twilio will only be charged after approval fires the execute step.');
        onSubmitted?.();
      } else {
        alert('Failed: ' + (res?.error || 'unknown'));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitle}>Provision new number ({env})</h3>
        <div style={s.field}>
          <label style={s.label}>Provider</label>
          <select value={provider} onChange={e => setProvider(e.target.value)} style={s.select}>
            <option value="aws">AWS End User Messaging (SMS)</option>
            <option value="twilio">Twilio (voice, auto-binds to {env === 'prod' ? 'NH-PROD' : 'NH-QA'} TwiML app)</option>
          </select>
        </div>
        {provider === 'aws' && (
          <div style={s.field}>
            <label style={s.label}>Type</label>
            <select value={type} onChange={e => setType(e.target.value)} style={s.select}>
              <option value="LONG_CODE">Long code (SMS)</option>
              <option value="TOLL_FREE">Toll-free (SMS + voice)</option>
            </select>
          </div>
        )}
        <div style={s.field}>
          <label style={s.label}>Preferred area code (optional)</label>
          <input type="text" placeholder="e.g. 469" value={areaCode} onChange={e => setAreaCode(e.target.value)} style={s.input} />
        </div>
        <div style={s.field}>
          <label style={s.label}>Auto-assign to practice on provision (optional)</label>
          <select value={autoAssignPracticeId} onChange={e => setAutoAssignPracticeId(e.target.value)} style={s.select}>
            <option value="">— skip auto-assign —</option>
            {practices.map(p => <option key={p.id} value={p.id}>#{p.id} · {p.name}</option>)}
          </select>
        </div>
        {autoAssignPracticeId && (
          <div style={s.field}>
            <label style={s.label}>Role</label>
            <select value={role} onChange={e => setRole(e.target.value)} style={s.select}>
              {provider === 'aws' ? (
                <>
                  <option value="sms_out_in">SMS out + in (auto-sets voice_out)</option>
                  <option value="voice_out">Voice outbound only</option>
                </>
              ) : (
                <option value="voice_in">Voice inbound</option>
              )}
            </select>
          </div>
        )}
        <div style={s.field}>
          <label style={s.label}>Notes (required)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={s.textarea} placeholder="Why is this number being provisioned?" />
        </div>
        <div style={s.hint}>Estimated cost: {provider === 'aws' ? `$${type === 'TOLL_FREE' ? '2.00' : '1.00'}/mo + $0.0075/msg` : '$1.00/mo (Twilio local)'} · approximate</div>
        <div style={s.modalActions}>
          <button style={s.btnSecondary} onClick={onClose}>Cancel</button>
          <button style={s.btnPrimary} onClick={submit} disabled={submitting || !notes}>
            {submitting ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

// UnassignButton removed — folded into RowActions kebab menu.

function AssignModal({ row, onClose, onSubmitted }) {
  const [practices, setPractices] = useState([]);
  const [practiceId, setPracticeId] = useState('');
  const [role, setRole] = useState('sms_out_in');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.request('/practices').then(res => {
      if (res?.success) setPractices((res.data || []).filter(p => p.enabled));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!practiceId) { setPreview(null); return; }
    const p = practices.find(x => String(x.id) === String(practiceId));
    const steps = [{
      provider: 'db',
      description: `phone_inventory.assigned_practice_id = ${practiceId} (${p?.name}), assigned_role = '${role}'`,
    }];
    if (role === 'sms_out_in') {
      steps.push({
        provider: 'db',
        description: `practices.sms_sender_phone = '${row.number_e164}' AND practices.voice_phone_number = '${row.number_e164}' (cascading)`,
      });
    } else if (role === 'voice_in') {
      steps.push({ provider: 'db', description: `practices.voice_inbound_phone = '${row.number_e164}'` });
    } else if (role === 'voice_out') {
      steps.push({ provider: 'db', description: `practices.voice_phone_number = '${row.number_e164}'` });
    }
    setPreview({ steps });
  }, [practiceId, role, practices, row.number_e164]);

  const submit = async () => {
    if (!practiceId) return alert('Pick a practice');
    setSubmitting(true);
    try {
      const res = await api.phoneCreateRequest({
        action: 'ASSIGN_TO_PRACTICE',
        target_number_id: row.id,
        target_practice_id: parseInt(practiceId, 10),
        params: { role },
        requester_notes: notes,
      });
      if (res?.success) {
        alert('Change request submitted for approval.');
        onSubmitted?.();
      } else {
        alert('Failed: ' + (res?.error || 'unknown'));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <h3 style={s.modalTitle}>Assign <code>{row.number_e164}</code></h3>
        <div style={s.field}>
          <label style={s.label}>Practice</label>
          <select value={practiceId} onChange={e => setPracticeId(e.target.value)} style={s.select}>
            <option value="">— pick —</option>
            {practices.map(p => <option key={p.id} value={p.id}>#{p.id} · {p.name}</option>)}
          </select>
        </div>
        <div style={s.field}>
          <label style={s.label}>Role</label>
          <select value={role} onChange={e => setRole(e.target.value)} style={s.select}>
            <option value="sms_out_in">SMS out + in (also becomes voice_out, cascading)</option>
            <option value="voice_in">Voice inbound only</option>
            <option value="voice_out">Voice outbound only</option>
          </select>
        </div>
        <div style={s.field}>
          <label style={s.label}>Notes (why)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={s.textarea} />
        </div>
        {preview && (
          <div style={s.preview}>
            <div style={s.previewHead}>Dry-run preview (approver will see this)</div>
            {preview.steps.map((step, i) => (
              <div key={i} style={s.previewStep}>
                <code style={s.badge}>{step.provider}</code> {step.description}
              </div>
            ))}
          </div>
        )}
        <div style={s.modalActions}>
          <button style={s.btnSecondary} onClick={onClose}>Cancel</button>
          <button style={s.btnPrimary} onClick={submit} disabled={submitting || !practiceId}>
            {submitting ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ByPracticeTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.phoneByPractice()
      .then(res => { if (res?.success) setRows(res.data || []); })
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div style={s.hint}>Loading…</div>;
  return (
    <div>
      {rows.length === 0 && <div style={s.hint}>No practices have numbers assigned yet.</div>}
      {rows.map(g => (
        <div key={g.practice_id} style={s.groupCard}>
          <div style={s.groupHead}>Practice #{g.practice_id} — {g.practice_name}</div>
          <table style={s.tableCompact}>
            <thead>
              <tr>
                <th style={s.th}>Provider</th>
                <th style={s.th}>Number</th>
                <th style={s.th}>Type</th>
                <th style={s.th}>Role</th>
              </tr>
            </thead>
            <tbody>
              {g.numbers.map(n => (
                <tr key={n.id} style={s.tr}>
                  <td style={s.td}>{n.provider.toUpperCase()}</td>
                  <td style={s.td}><code>{n.number_e164}</code></td>
                  <td style={s.td}>{n.type}</td>
                  <td style={s.td}>{n.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function RequestsTab({ currentUserId }) {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.phoneListRequests({ status: statusFilter, limit: 100 })
      .then(res => { if (res?.success) setRows(res.data || []); })
      .finally(() => setLoading(false));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    const notes = window.prompt('Approval notes (visible in audit):');
    if (notes == null) return;
    const res = await api.phoneApproveRequest(id, notes);
    if (res?.success) load();
    else alert('Approve failed: ' + (res?.error || 'unknown'));
  };
  const reject = async (id) => {
    const notes = window.prompt('Rejection reason:');
    if (!notes) return;
    const res = await api.phoneRejectRequest(id, notes);
    if (res?.success) load();
    else alert('Reject failed: ' + (res?.error || 'unknown'));
  };
  const cancel = async (id) => {
    if (!window.confirm('Cancel this pending request?')) return;
    const res = await api.phoneCancelRequest(id);
    if (res?.success) load();
    else alert('Cancel failed: ' + (res?.error || 'unknown'));
  };

  return (
    <div>
      <div style={s.toolbar}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={s.select}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="done">Done</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button style={s.btnSecondary} onClick={load}>Refresh</button>
      </div>
      {loading ? <div style={s.hint}>Loading…</div> : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>#</th>
              <th style={s.th}>Action</th>
              <th style={s.th}>Target</th>
              <th style={s.th}>Practice</th>
              <th style={s.th}>Requester</th>
              <th style={s.th}>Notes</th>
              <th style={s.th}>Preview</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const isMine = r.requester_user_id === currentUserId;
              return (
                <tr key={r.id} style={s.tr}>
                  <td style={s.td}>{r.id}</td>
                  <td style={s.td}><code>{r.action}</code></td>
                  <td style={s.td}>{r.target_number ? <code>{r.target_number}</code> : '—'}</td>
                  <td style={s.td}>{r.target_practice_name || '—'}</td>
                  <td style={s.td}>{[r.requester_first_name, r.requester_last_name].filter(Boolean).join(' ') || `#${r.requester_user_id}`}</td>
                  <td style={{ ...s.td, maxWidth: 240 }}>{r.requester_notes || <span style={s.muted}>—</span>}</td>
                  <td style={s.td}>
                    <details><summary>steps</summary>
                      <pre style={s.pre}>{JSON.stringify(r.dry_run_preview, null, 2)}</pre>
                    </details>
                  </td>
                  <td style={s.td}>
                    {r.status === 'pending' && !isMine && (
                      <>
                        <button style={s.btnLink} onClick={() => approve(r.id)}>Approve</button>
                        {' · '}
                        <button style={s.btnLinkDanger} onClick={() => reject(r.id)}>Reject</button>
                      </>
                    )}
                    {r.status === 'pending' && isMine && (
                      <>
                        <span style={s.muted}>Awaiting other approver</span>
                        {' · '}
                        <button style={s.btnLinkDanger} onClick={() => cancel(r.id)}>Cancel</button>
                      </>
                    )}
                    {r.status !== 'pending' && <span style={s.muted}>{r.status}</span>}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', padding: 24, color: '#6b7280' }}>No {statusFilter} requests.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.phoneAudit({ limit: 200 })
      .then(res => { if (res?.success) setRows(res.data || []); })
      .finally(() => setLoading(false));
  }, []);

  const downloadCsv = () => {
    // Client-side CSV generation from the loaded rows. Keeps sensitive
    // before/after JSON in escaped-quotes form; downloads directly.
    const escape = (v) => {
      if (v == null) return '';
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      return '"' + s.replace(/"/g, '""') + '"';
    };
    const header = ['occurred_at', 'request_id', 'event_type', 'actor', 'provider', 'outcome', 'error', 'before_state', 'after_state', 'api_calls'];
    const lines = [header.join(',')];
    for (const r of rows) {
      lines.push([
        r.occurred_at, r.request_id, r.event_type,
        [r.actor_first_name, r.actor_last_name].filter(Boolean).join(' ') || (r.actor_user_id ? `#${r.actor_user_id}` : 'system'),
        r.provider, r.outcome, r.error,
        r.before_state, r.after_state, r.api_calls,
      ].map(escape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `phone-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  if (loading) return <div style={s.hint}>Loading…</div>;
  return (
    <>
    <div style={s.toolbar}>
      <button style={s.btnSecondary} onClick={downloadCsv} disabled={rows.length === 0}>Download CSV</button>
      <span style={s.hint}>{rows.length} row{rows.length === 1 ? '' : 's'}</span>
    </div>
    <table style={s.table}>
      <thead>
        <tr>
          <th style={s.th}>Time</th>
          <th style={s.th}>Req #</th>
          <th style={s.th}>Event</th>
          <th style={s.th}>Actor</th>
          <th style={s.th}>Provider</th>
          <th style={s.th}>Outcome</th>
          <th style={s.th}>Detail</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id} style={s.tr}>
            <td style={s.td}>{new Date(r.occurred_at).toLocaleString()}</td>
            <td style={s.td}>{r.request_id || '—'}</td>
            <td style={s.td}><code>{r.event_type}</code></td>
            <td style={s.td}>{[r.actor_first_name, r.actor_last_name].filter(Boolean).join(' ') || (r.actor_user_id ? `#${r.actor_user_id}` : 'system')}</td>
            <td style={s.td}>{r.provider || '—'}</td>
            <td style={s.td}>{r.outcome || '—'}</td>
            <td style={s.td}>
              <details><summary>view</summary>
                <pre style={s.pre}>{JSON.stringify({ before: r.before_state, after: r.after_state, api_calls: r.api_calls, error: r.error }, null, 2)}</pre>
              </details>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </>
  );
}

const s = {
  page: { padding: 20 },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 },
  title: { margin: 0, fontSize: 22, fontWeight: 700 },
  subtitle: { margin: '4px 0 0', color: '#6b7280', fontSize: 13 },
  envBadge: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, fontSize: 12, color: '#6b7280' },
  envKey: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 1 },
  envVal: (env) => ({
    padding: '2px 10px', borderRadius: 999, fontWeight: 700,
    background: env === 'prod' ? '#fee2e2' : '#dbeafe',
    color: env === 'prod' ? '#991b1b' : '#1e40af',
  }),
  envHint: { fontSize: 11 },
  tabbar: { display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 16 },
  tab: { padding: '8px 14px', border: 'none', background: 'transparent', color: '#6b7280', cursor: 'pointer', fontSize: 13, fontWeight: 500, borderBottom: '2px solid transparent', marginBottom: -1 },
  tabActive: { color: '#2563eb', borderBottomColor: '#2563eb' },
  body: {},
  toolbar: { display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' },
  hint: { color: '#6b7280', fontSize: 12, marginBottom: 8 },
  select: { padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' },
  btnPrimary: { padding: '6px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  btnSecondary: { padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13 },
  btnLink: { background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, textDecoration: 'underline', padding: 0 },
  btnLinkDanger: { background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13, textDecoration: 'underline', padding: 0 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff', border: '1px solid #e5e7eb' },
  tableCompact: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 },
  th: { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', color: '#6b7280', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600 },
  tr: {},
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  muted: { color: '#9ca3af' },
  groupCard: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 },
  groupHead: { fontWeight: 600, marginBottom: 4 },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '10vh' },
  modal: { background: '#fff', width: 'min(560px, 92vw)', borderRadius: 10, padding: 20, boxShadow: '0 20px 40px rgba(0,0,0,0.25)' },
  modalTitle: { margin: 0, marginBottom: 12, fontSize: 16 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 },
  label: { fontSize: 12, color: '#6b7280', fontWeight: 500 },
  textarea: { padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical' },
  preview: { border: '1px solid #dbeafe', background: '#eff6ff', borderRadius: 6, padding: 10 },
  previewHead: { fontSize: 11, fontWeight: 600, color: '#1e40af', marginBottom: 6 },
  previewStep: { fontSize: 12, marginBottom: 3, color: '#1e40af' },
  badge: { background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: 3, fontSize: 10, marginRight: 4 },
  pre: { background: '#f9fafb', border: '1px solid #e5e7eb', padding: 8, borderRadius: 4, fontSize: 11, overflow: 'auto', maxHeight: 300 },
  menu: { position: 'absolute', top: '100%', right: 0, zIndex: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 8px 20px rgba(0,0,0,0.12)', minWidth: 260, padding: 4, marginTop: 4 },
  menuItem: { display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: 'transparent', fontSize: 13, cursor: 'pointer', color: '#111827', borderRadius: 4 },
  input: { padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'monospace' },
  warnBox: { background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6, padding: 10, fontSize: 12, color: '#92400e', marginBottom: 10 },
  driftBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#991b1b', marginBottom: 10 },
};

export default PhoneManagement;
